import { extendTheme } from '@chakra-ui/react'
import { Z_INDEX } from 'constants/common'
import { quillEditorStyle } from '../screens/custom-pages/components/widgets/TextWidget/TextEditor'
import { colors } from './colors'
import { Accordion } from './components/Accordion'
import { Alert } from './components/Alert'
import { Avatar } from './components/Avatar'
import { Badge } from './components/Badge'
import { Breadcrumb } from './components/Breadcrumb'
import { Button } from './components/Button'
import { Checkbox } from './components/Checkbox'
import { Drawer } from './components/Drawer'
import { Form } from './components/Form'
import { FormError } from './components/FormError'
import { FormLabel } from './components/FormLabel'
import { Heading } from './components/Heading'
import { Input } from './components/Input'
import { Link } from './components/Link'
import { Menu } from './components/Menu'
import { Modal } from './components/Modal'
import { NumberInput } from './components/NumberInput'
import { Popover } from './components/Popover'
import { Progress } from './components/Progress'
import { Radio } from './components/Radio'
import { Select } from './components/Select'
import { Switch } from './components/Switch'
import { Table } from './components/Table'
import { Tabs } from './components/Tabs'
import { Tag } from './components/Tag'
import { Textarea } from './components/Textarea'
import { Tooltip } from './components/Tooltip'
import { fontSizes } from './fontSizes'
import { graphiqlStyles } from './graphiql'
import { navLinkStyles } from './navlink'
import { prismjsStyles } from './prismjs'
import { reactGridLayoutStyle } from './react-grid-layoutsplit'
import { reactSplitStyle } from './react-split'
import { textStyles } from './textStyles'
import { voyagerStyles } from './voyager'

// Big redesign is planned for the entire app.
// Let's add tag #DESIGN_2023 on places where new design
// is mentioned so we can find it and identify it easily.

export const theme = extendTheme({
  fonts: {
    heading: `'Lato', sans-serif`,
    body: `'Lato', sans-serif`,
  },
  fontSizes,
  ...textStyles, // #DESIGN_2023
  sizes: {
    4.5: '1.125rem',
    76: '19rem',
    11: '2.75rem',
  },
  space: {
    0.75: '0.187rem',
    11: '2.75rem',
    22: '5.5rem',
    26: '6.5rem',
    27: '6.75rem',
    30: '7.5rem',
  },
  colors,
  shadows: {
    outline: '0px 0px 0px 1px white, 0px 0px 4px 2px #15B1D7',
    product: {
      xs: '0px 3.23002px 2.58402px 0px rgba(111, 114, 132, 0.04)',
      sm: '0px 7.76218px 6.20974px 0px rgba(111, 114, 132, 0.06)',
      md: '0px 14.6155px 11.6924px 0px rgba(111, 114, 132, 0.08)',
      lg: '0px 26.0715px 20.8572px 0px rgba(111, 114, 132, 0.09)',
      xl: '0px 48.7639px 39.0112px 0px rgba(111, 114, 132, 0.11)',
      '2xl': '0px 116.723px 93.3782px 0px rgba(111, 114, 132, 0.15)',
    },
  },
  components: {
    Accordion,
    Alert,
    Avatar,
    Badge,
    Breadcrumb,
    Button,
    Checkbox,
    Drawer,
    Form,
    FormError,
    FormLabel,
    Heading,
    Input,
    Link,
    Menu,
    Modal,
    NumberInput,
    Popover,
    Progress,
    Radio,
    Select,
    Switch,
    Table,
    Tabs,
    Tag,
    Textarea,
    Tooltip,
  },
  styles: {
    global: {
      ...voyagerStyles,
      ...graphiqlStyles,
      ...prismjsStyles,
      ...reactSplitStyle,
      ...reactGridLayoutStyle,
      ...navLinkStyles,
      ...quillEditorStyle,
      // font-family to be consistent in firefox <select>
      body: {
        fontSize: 'sm',
        fontFamily: "'Lato', sans-serif",
        // fix issue with scrollbar appearing on the whole page when scrolling side menu
        overflow: 'hidden',
      },
      'select, option': {
        font: '-moz-pull-down-menu',
      },
      'option::first-letter': {
        color: 'red',
        textTransform: 'uppercase',
      },
      'div[data-type="Resizer"]': {
        zIndex: Z_INDEX.DEFAULT,
      },
      // prevent items jumping when dragging or dropping
      '.drag-and-drop-item~div': {
        transform: 'translate(0px, 0px) !important',
      },
      // react-d3-tree overrides
      '.rd3t-link': {
        stroke: 'var(--chakra-colors-blueGray-300) !important',
      },
      '.rd3t-node': {
        cursor: 'inherit !important',
      },
      // workaround until https://github.com/chakra-ui/chakra-ui/issues/3814 will be fixed
      '.chakra-portal > div': {
        position: 'absolute',
        left: '0px',
        top: '0px',
      },
      // Cursor styles for https://github.com/tameemsafi/typewriterjs
      '.Typewriter__cursor': {
        color: 'product.text.dark.bodySubtle',
        bg: 'product.text.dark.bodySubtle',
        width: 3,
        display: 'inline-block',
      },
      // hide dropdown menu when menu button is not in the current viewport,
      // this will prevent dropdown in the scrollable table to be visible
      // above table when scrolling down while the dropdown is still open
      '[data-popper-reference-hidden]': {
        opacity: '0',
        pointerEvents: 'none',
      },
      '::placeholder': {
        color: 'blueGray.400',
      },
    },
  },
})
